import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "../studio/studio.css";
import "./liveHost.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { startHunt, subscribeHuntStart } from "../../../utils/huntFirebase";
import { getLiveHuntStatus, reviewCheckpointPhoto, adminCompleteRound, exportHuntResults } from "../../../functions/checkpointGame";
import { updateThemeData } from "../../functions/updateThemeData";
import { TourStartButton } from "../../tour/AdminTour";
import { s3PublicUrl } from "../../../utils/s3PublicUrl";

function downloadCsv(filename, rows) {
  const csvField = (value) => {
    const s = String(value ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ["Rank", "Team #", "Team name", "Captain", "War cry", "Points", "Time", "Rounds completed", "Hints used"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push(
      [r.rank, r.teamNumber, r.teamName, r.captainName, r.warCry, r.totalScore, r.time, `${r.roundsCompleted}/${r.totalRounds}`, r.hintsUsed]
        .map(csvField)
        .join(",")
    );
  });
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const POLL_MS = 5000;

const STATUS_LABEL = {
  on_pace: "On pace",
  stuck: "Stuck",
  photo: "Review",
  done: "Done",
  not_started: "Not started",
};

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatClock(totalSeconds) {
  if (totalSeconds == null) return "—:—";
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function LiveHost() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const hunt = data || {};
  const [started, setStarted] = useState(false);
  const [starting, setStarting] = useState(false);

  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null); // team being reviewed in the modal
  const [bonusPoints, setBonusPoints] = useState("");
  const [exporting, setExporting] = useState(false);
  const pollRef = useRef(null);

  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [timeVisible, setTimeVisible] = useState(true);
  const [approveContentEnabled, setApproveContentEnabled] = useState(false);
  const liveSettingsLoadedRef = useRef(false);

  // Raw theme fields come straight from `data` here (unlike Studio, which runs everything
  // through normalizeHunt's coerceString first) — themesdata's generic JSON.parse-everything read
  // means a purely-numeric string an admin typed (e.g. a Client field of just "2026") silently
  // comes back as an actual number, not a string, and crashes any .toUpperCase()/.trim() call.
  const huntName = String(hunt.huntName ?? "");
  const client = String(hunt.client ?? "");

  useEffect(() => {
    document.title = huntName.trim() ? `${huntName} — Live Host` : "ExtraMile Play Live Host";
  }, [huntName]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the toggles from saved hunt data once (not on every theme refetch, so an in-flight
  // toggle here doesn't get stomped by a stale value coming back from the server).
  useEffect(() => {
    if (!hunt || liveSettingsLoadedRef.current) return;
    if (
      hunt.liveLeaderboardEnabled === undefined &&
      hunt.liveTimeVisible === undefined &&
      hunt.approveContentEnabled === undefined
    )
      return;
    liveSettingsLoadedRef.current = true;
    setLeaderboardEnabled(hunt.liveLeaderboardEnabled !== false);
    setTimeVisible(hunt.liveTimeVisible !== false);
    setApproveContentEnabled(hunt.approveContentEnabled === true);
  }, [hunt]);

  const toggleLeaderboard = async () => {
    const next = !leaderboardEnabled;
    setLeaderboardEnabled(next);
    try {
      await updateThemeData({
        payload: { data: { liveLeaderboardEnabled: next }, organizationId, sessionId, currentTheme },
        token: adminToken,
      });
    } catch (e) {
      setLeaderboardEnabled(!next);
      Swal.fire("Error", e?.message || "Failed to update leaderboard visibility.", "error");
    }
  };

  const toggleTimeVisible = async () => {
    const next = !timeVisible;
    setTimeVisible(next);
    try {
      await updateThemeData({
        payload: { data: { liveTimeVisible: next }, organizationId, sessionId, currentTheme },
        token: adminToken,
      });
    } catch (e) {
      setTimeVisible(!next);
      Swal.fire("Error", e?.message || "Failed to update timer visibility.", "error");
    }
  };

  const toggleApproveContent = async () => {
    const next = !approveContentEnabled;
    setApproveContentEnabled(next);
    try {
      await updateThemeData({
        payload: { data: { approveContentEnabled: next }, organizationId, sessionId, currentTheme },
        token: adminToken,
      });
    } catch (e) {
      setApproveContentEnabled(!next);
      Swal.fire("Error", e?.message || "Failed to update approve content setting.", "error");
    }
  };

  const handleExportResults = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const resp = await exportHuntResults({ token: adminToken, themeName: currentTheme });
      downloadCsv(`${(huntName || "hunt").replace(/\s+/g, "_").toLowerCase()}_export-csv.csv`, resp.rows || []);
    } catch (e) {
      Swal.fire("Error", e?.message || "Failed to export results.", "error");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!organizationId || !sessionId) return undefined;
    const unsubscribe = subscribeHuntStart(organizationId, sessionId, (huntStart) => {
      setStarted(!!huntStart?.startedAt);
    });
    return () => unsubscribe();
  }, [organizationId, sessionId]);

  const refresh = useCallback(async () => {
    if (!adminToken) return;
    try {
      const resp = await getLiveHuntStatus({ token: adminToken, themeName: currentTheme });
      setTeams(resp.teams || []);
      setStats(resp.stats || null);
    } catch {
      // ignore — try again next poll
    }
  }, [adminToken, currentTheme]);

  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [refresh]);

  const handleStartHunt = async () => {
    if (!organizationId || !sessionId || starting || started) return;
    setStarting(true);
    try {
      await startHunt(organizationId, sessionId);
    } catch (e) {
      Swal.fire("Error", e?.message || "Failed to start the hunt.", "error");
    } finally {
      setStarting(false);
    }
  };

  const handleMarkComplete = async (team) => {
    try {
      await adminCompleteRound({ token: adminToken, stageId: team.stageId, themeName: currentTheme });
      refresh();
    } catch (e) {
      Swal.fire("Error", e?.message || "Failed to mark round complete.", "error");
    }
  };

  const handleReview = async (approve) => {
    if (!reviewTarget) return;
    try {
      await reviewCheckpointPhoto({
        token: adminToken,
        stageId: reviewTarget.stageId,
        approve,
        bonusPoints: approve && bonusPoints ? bonusPoints : undefined,
        themeName: currentTheme,
      });
      setReviewTarget(null);
      setBonusPoints("");
      refresh();
    } catch (e) {
      Swal.fire("Error", e?.message || "Failed to review photo.", "error");
    }
  };

  const statTiles = [
    { label: "Teams playing", value: stats?.teamsPlaying ?? "—" },
    { label: "Rounds done", value: stats ? `${stats.roundsDone} / ${stats.totalPossibleRounds}` : "—" },
    { label: "Photos pending", value: stats?.photosPending ?? "—" },
    { label: "Stuck over 8 min", value: stats?.stuckOver8Min ?? "—" },
    { label: "Fastest team", value: stats?.fastestTeamName || "—" },
  ];

  return (
    <div className="live-shell">
      <div className="live-breadcrumb-row">
        <button className="studio-btn studio-btn--ghost" onClick={() => navigate("/admin/rules")}>
          Back
        </button>
      </div>

      <header className="live-header">
        <div className="live-header-left">
          <span className="live-badge">
            <span className="live-dot" /> LIVE
          </span>
          <div>
            <div className="live-eyebrow">
              {(huntName || "Untitled hunt").toUpperCase()}
              {client ? ` · ${client.toUpperCase()}` : ""}
            </div>
            <h1 className="live-title">Live host control</h1>
          </div>
        </div>
        <div className="live-header-right">
          <span className="live-elapsed">Elapsed {formatClock(stats?.elapsedSeconds)}</span>
          <button
            className="studio-btn studio-btn--primary"
            data-tour="live-start-hunt-btn"
            onClick={handleStartHunt}
            disabled={starting || started}
          >
            {started ? "Hunt started" : starting ? "Starting..." : "Start hunt"}
          </button>
          <TourStartButton />
        </div>
      </header>

      <div className="live-body">
        <div className="live-settings-row" data-tour="live-settings-row">
          <label className="live-switch-row">
            <span
              className={`live-switch${leaderboardEnabled ? " live-switch--on" : ""}`}
              role="switch"
              aria-checked={leaderboardEnabled}
              onClick={toggleLeaderboard}
            >
              <span className="live-switch-knob" />
            </span>
            Leaderboard visible to players
          </label>
          <label className="live-switch-row">
            <span
              className={`live-switch${timeVisible ? " live-switch--on" : ""}`}
              role="switch"
              aria-checked={timeVisible}
              onClick={toggleTimeVisible}
            >
              <span className="live-switch-knob" />
            </span>
            Timer visible to players
          </label>
          <label className="live-switch-row" data-tour="live-approve-content-toggle">
            <span
              className={`live-switch${approveContentEnabled ? " live-switch--on" : ""}`}
              role="switch"
              aria-checked={approveContentEnabled}
              onClick={toggleApproveContent}
            >
              <span className="live-switch-knob" />
            </span>
            Approve content
          </label>
          <button
            type="button"
            className="studio-btn studio-btn--ghost"
            data-tour="live-export-csv-btn"
            style={{ marginLeft: "auto" }}
            onClick={handleExportResults}
            disabled={exporting || !stats?.allFinished}
            title={stats?.allFinished ? "Download results as CSV" : "Available once every team has finished"}
          >
            {exporting ? "Exporting..." : "Export results (CSV)"}
          </button>
        </div>

        <div className="live-stats-row" data-tour="live-stats-row">
          {statTiles.map((s) => (
            <div className="live-stat-tile" key={s.label}>
              <div className="live-stat-label">{s.label.toUpperCase()}</div>
              <div className="live-stat-value">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="live-team-grid" data-tour="live-team-grid">
          {teams.map((t, i) => (
            <div className={`live-team-card live-team-card--${t.status}`} key={t.stageId}>
              <div className="live-team-card-top">
                <div className="live-team-eyebrow">TEAM {String(t.teamNumber || i + 1).padStart(2, "0")}</div>
                <span className={`live-status-badge live-status-badge--${t.status}`}>
                  {t.status === "photo" ? `Review ${capitalize(t.pendingPhoto?.answerType) || "Photo"}` : STATUS_LABEL[t.status]}
                </span>
              </div>
              <div className="live-team-name">{t.teamName}</div>
              <div className="live-team-sub">
                {t.roundsCompleted} / {t.totalRounds} · {t.checkpointName}
              </div>
              <div className="live-progress-track">
                {Array.from({ length: t.totalRounds || 0 }).map((_, ri) => (
                  <span
                    key={ri}
                    className={`live-progress-seg${ri < t.roundsCompleted ? " live-progress-seg--done" : ""}`}
                  />
                ))}
              </div>
              <div className="live-team-footer">
                <span className="live-muted">
                  {t.status === "done" ? "Finished" : `On this round ${formatClock(t.onRoundSeconds)}`}
                </span>
                {t.status === "photo" ? (
                  <button
                    className="studio-link-btn"
                    onClick={() => {
                      setBonusPoints("");
                      setReviewTarget(t);
                    }}
                  >
                    Review {t.pendingPhoto?.answerType || "photo"}
                  </button>
                ) : (
                  <button
                    className="studio-link-btn"
                    onClick={() => handleMarkComplete(t)}
                    disabled={t.status === "done" || t.status === "not_started"}
                  >
                    Mark complete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {teams.length === 0 && (
          <div className="studio-hint">No teams have registered yet.</div>
        )}
      </div>

      {reviewTarget && (
        <div className="live-modal-backdrop" onClick={() => setReviewTarget(null)}>
          <div className="live-modal" onClick={(e) => e.stopPropagation()}>
            <div className="live-modal-eyebrow">
              REVIEW {(reviewTarget.pendingPhoto?.answerType || "photo").toUpperCase()} · TEAM {reviewTarget.teamNumber}
            </div>
            <div className="live-modal-team">{reviewTarget.teamName}</div>
            {reviewTarget.pendingPhoto?.answer && (
              reviewTarget.pendingPhoto.answerType === "video" ? (
                <video className="live-modal-photo" src={s3PublicUrl(reviewTarget.pendingPhoto.answer)} controls />
              ) : reviewTarget.pendingPhoto.answerType === "audio" ? (
                <audio className="live-modal-audio" src={s3PublicUrl(reviewTarget.pendingPhoto.answer)} controls />
              ) : (
                <img
                  className="live-modal-photo"
                  src={s3PublicUrl(reviewTarget.pendingPhoto.answer)}
                  alt="Team submission"
                />
              )
            )}
            <div className="live-modal-field">
              <label htmlFor="live-bonus-points">Bonus points (optional, 5-50)</label>
              <input
                id="live-bonus-points"
                type="number"
                className="studio-input"
                placeholder="e.g. 20"
                value={bonusPoints}
                onChange={(e) => setBonusPoints(e.target.value)}
                onBlur={() => {
                  if (bonusPoints === "") return;
                  const n = Number(bonusPoints);
                  setBonusPoints(Number.isFinite(n) ? String(Math.min(50, Math.max(5, Math.round(n)))) : "");
                }}
              />
            </div>
            <div className="live-modal-actions">
              <button className="studio-btn studio-btn--ghost" onClick={() => handleReview(false)}>
                Reject
              </button>
              <button className="studio-btn studio-btn--primary" onClick={() => handleReview(true)}>
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveHost;
